(function () {
  'use strict';

  function initQuiz(root) {
    var sectionId = root.dataset.sectionId;
    var config = (window.pqConfig && window.pqConfig[sectionId]) || {};
    var steps = Array.prototype.slice.call(root.querySelectorAll('[data-pq-step]'));
    var resultScreen = root.querySelector('[data-pq-result]');
    var stepsWrapper = root.querySelector('[data-pq-steps]');
    var answers = {};
    var currentIndex = 0;

    // ------------------------------------------------------------------
    // NEW: isBraProduct — keeps only actual bras in the results, so
    // briefs/bikinis/hi-cuts/crops etc. never show on the quiz results
    // page even though they're in the same catalog. Your products carry
    // a tag like "x-prodcat:BRA" on bras (visible in your product admin
    // screenshots) — this checks for that tag first since it's the most
    // reliable signal, and falls back to checking product_type/tags for
    // the word "bra" while explicitly excluding bottom-wear/short/crop
    // keywords, in case a product is missing the prodcat tag.
    // ------------------------------------------------------------------
    function isBraProduct(p) {
      var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var type = (p.product_type || '').toLowerCase();

      // Primary signal: the exact prodcat tag your catalog already uses.
      var hasBraProdcatTag = tags.some(function (t) { return t.indexOf('prodcat:bra') !== -1; });
      if (hasBraProdcatTag) return true;

      // If a different prodcat tag is present, trust it over any fallback
      // guesswork below — it means this product was deliberately
      // categorized as something other than a bra.
      var hasOtherProdcatTag = tags.some(function (t) { return t.indexOf('prodcat:') !== -1; });
      if (hasOtherProdcatTag) return false;

      // Fallback for products with no prodcat tag at all: check
      // product_type / tags for "bra", excluding common bottom-wear and
      // non-bra category words.
      var excludeKeywords = ['brief', 'bikini', 'hipster', 'thong', 'boyleg', 'short', 'knicker', 'g-string', 'panty', 'panties', 'undershort', 'crop'];
      var typeExcluded = excludeKeywords.some(function (k) { return type.indexOf(k) !== -1; });
      var tagExcluded = tags.some(function (t) { return excludeKeywords.some(function (k) { return t.indexOf(k) !== -1; }); });
      if (typeExcluded || tagExcluded) return false;

      return type.indexOf('bra') !== -1;
    }

    // ------------------------------------------------------------------
    // fetchAllProducts — /products.json (and /collections/x/products.json)
    // only ever return a single page, capped at 250 products, in the
    // store's default listing order. Any product sitting past that first
    // page was never reaching the browser at all, so it could never be
    // matched — regardless of its tags/variants/metafields. This walks
    // page=1, page=2, ... until Shopify returns a short page (fewer than
    // 250 items), then hands back the full combined list. Everything
    // downstream (filtering, sorting, rendering) is unchanged.
    // ------------------------------------------------------------------
    function fetchAllProducts(baseUrl) {
      var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
      var all = [];

      function getPage(page) {
        return fetch(baseUrl + sep + 'page=' + page)
          .then(function (res) { return res.json(); })
          .then(function (data) {
            var products = data.products || [];
            all = all.concat(products);
            if (products.length === 250) {
              return getPage(page + 1); // keep going until a short page
            }
            return all;
          });
      }

      return getPage(1);
    }

    // ------------------------------------------------------------------
    // Size-adjustment logic.
    // Band scale 10/12/14/16/18/20, cup scale B/C/D/DD/E (EU tab, Step 2
    // tabs block). If you change those values in the theme editor, update
    // these two arrays to match, or the adjustment silently does nothing
    // (unknown values are left unchanged by shiftInScale).
    //
    // underwire_fit (Step 4) and bridge_fit (Step 5) are now wired in as
    // cup corrections, based on the tip text in your own theme editor
    // ("If your underwire digs into your ribs, it's too small — try a
    // larger cup size" / bridge not lying flat = cup too small). Both
    // point the same direction as cup_fit's "digs in" case.
    //
    // IMPORTANT: 'It digs in' for underwire_fit reuses the same label
    // text as band_fit — verify that's the actual option label in your
    // theme editor (not something like "It pinches"). The bridge_fit
    // label below ("It doesn't lie flat") is my best guess from the
    // partially-visible tip text in your screenshot — please confirm the
    // exact label against your Step 5 block and correct it if it differs.
    // Unmatched labels are silently ignored (safe — never breaks
    // anything), so a wrong guess here just means no adjustment happens,
    // not an error.
    // ------------------------------------------------------------------
    var BAND_SCALE = ['10', '12', '14', '16', '18', '20'];
    var CUP_SCALE = ['B', 'C', 'D', 'DD', 'E'];

    function shiftInScale(scale, value, steps) {
      var idx = scale.indexOf(String(value));
      if (idx === -1) return value; // unknown value — leave as-is
      var newIdx = Math.min(scale.length - 1, Math.max(0, idx + steps));
      return scale[newIdx];
    }

    // Case-insensitive, whitespace-trimmed equality check, used for every
    // fit-check comparison below so small text/casing differences between
    // this file and your theme editor labels don't silently break things.
    function answerIs(key, expected) {
      return String(answers[key] || '').toLowerCase().trim() === expected.toLowerCase().trim();
    }

    function computeAdjustedSize(band, cup) {
      var bandShift = 0;
      var cupShift = 0;

      // Band-fit correction (Step 3, answer key: band_fit)
      // Live options: "It rides up" / "It stays put" / "It digs in"
      if (answerIs('band_fit', 'It rides up')) bandShift -= 1; // band too big
      if (answerIs('band_fit', 'It digs in')) bandShift += 1;  // band too small

      // Underwire-fit correction (Step 4, answer key: underwire_fit)
      // Live options: "It digs into my breasts and sides" /
      // "It fits comfortably under and around my breasts" / "It pokes my
      // underarms". Digging into breasts/sides = cup too small.
      if (answerIs('underwire_fit', 'It digs into my breasts and sides')) cupShift += 1;

      // Bridge-fit correction (Step 5, answer key: bridge_fit)
      // Live options: "It lays flat against my body" / "It lifts away
      // from my body". Lifting away = cup too small (cups can't fully
      // close, so the bridge is pushed forward).
      if (answerIs('bridge_fit', 'It lifts away from my body')) cupShift += 1;

      // Cup-fit correction (Step 6, answer key: cup_fit)
      // Live options: "The cups gape or the fabric puckers" / "The cups
      // fit perfectly around my breasts" / "The cups dig into my breasts"
      if (answerIs('cup_fit', 'The cups gape or the fabric puckers')) cupShift -= 1; // cup too big
      if (answerIs('cup_fit', 'The cups dig into my breasts')) cupShift += 1;        // cup too small

      // Sister-sizing: band and cup move opposite directions to keep
      // roughly the same cup volume when the band size changes.
      var sisterCupShift = -bandShift;

      return {
        band: shiftInScale(BAND_SCALE, band, bandShift),
        cup: shiftInScale(CUP_SCALE, cup, cupShift + sisterCupShift)
      };
    }

    // ------------------------------------------------------------------
    // Style-preference mapping for Steps 7-10 (straps fit, breast
    // position, breast shape, symmetry). These never exclude a product —
    // they only reorder the already-matched results so products carrying
    // these tags float to the top.
    //
    // Matching is case-insensitive and whitespace-trimmed, so small
    // differences between this table and your actual option text in the
    // theme editor won't silently break it.
    //
    // If your products use different tag names than the right-hand
    // column, just change the tag strings below — nothing else needs to
    // change.
    // ------------------------------------------------------------------
    var STYLE_PREFERENCE_MAP = {
      // Live options: "They dig into my skin" / "They rest comfortably on
      // my shoulders" / "They slip off"
      straps_fit: {
        'they dig into my skin': 'wide-straps',
        'they slip off': 'adjustable-straps'
      },
      // Live options: "Close-set (less than two fingers)" /
      // "Average-set (exactly two fingers)" / "Wide-set (more than two
      // fingers)" — matched on the full label text, since your quiz
      // includes the finger-count description in the option itself.
      breast_position: {
        'close-set (less than two fingers)': 'center-gore',
        'wide-set (more than two fingers)': 'plunge'
      },
      // Live options: "Round" / "Teardrop-shaped" / "Relaxed"
      breast_shape: {
        'round': 'balconette',
        'teardrop-shaped': 'full-cup',
        'relaxed': 'push-up'
      },
      // Live options: "More or less symmetrical" / "Slightly asymmetrical"
      symmetry: {
        'slightly asymmetrical': 'removable-pads'
      }
    };

    // Reads whatever the customer answered on Steps 7-10 and turns it
    // into a flat list of preference tags to sort by. Steps that were
    // never added, skipped, or answered with something outside the map
    // are simply ignored — this never throws and never blocks matching.
    function getPreferenceTags() {
      var tags = [];
      Object.keys(STYLE_PREFERENCE_MAP).forEach(function (answerKey) {
        var answer = answers[answerKey];
        if (!answer) return;
        var map = STYLE_PREFERENCE_MAP[answerKey];
        var tag = map[String(answer).toLowerCase().trim()];
        if (tag) tags.push(tag);
      });
      return tags;
    }

    // The other quiz answers configured under "Other answer keys to use
    // as tags" (style, brand, padding, etc.) are read here and turned
    // into soft sorting preferences (never hard filters), same treatment
    // as the Step 7-10 style prefs above.
    function getTagAnswerPreferences() {
      var tags = [];
      (config.tagAnswerKeys || []).forEach(function (key) {
        var val = answers[key];
        if (!val) return;
        if (Array.isArray(val)) {
          val.forEach(function (v) { if (v) tags.push(String(v)); });
        } else {
          tags.push(String(val));
        }
      });
      return tags;
    }

    // Stable sort: products carrying more of the customer's preference
    // tags come first. If preferenceTags is empty, the array order is
    // returned untouched. If no product carries any of the tags, every
    // score is 0 and the original order is preserved — this can never
    // make results worse, only reorder when there's a genuine signal.
    function sortByPreferenceTags(products, preferenceTags) {
      if (!preferenceTags || !preferenceTags.length) return products;
      var lowerPrefs = preferenceTags.map(function (t) { return String(t).toLowerCase(); });

      return products
        .map(function (p, i) {
          var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
          var score = lowerPrefs.reduce(function (sum, pref) {
            return sum + (tags.indexOf(pref) !== -1 ? 1 : 0);
          }, 0);
          return { p: p, i: i, score: score };
        })
        .sort(function (a, b) {
          if (b.score !== a.score) return b.score - a.score;
          return a.i - b.i; // stable — preserve original order on ties
        })
        .map(function (x) { return x.p; });
    }

    // Measures the currently-active step's fixed footer height and
    // exposes it as a CSS variable (--pq-footer-height) on the root, so
    // the required-message box (position: fixed in the CSS) can pin
    // itself exactly above the footer instead of sitting inline in the
    // content flow where it could end up out of view until scrolled.
    function updateFooterHeightVar() {
      var activeStep = steps[currentIndex];
      var footer = activeStep && activeStep.querySelector('.pq-step-footer');
      if (footer) {
        root.style.setProperty('--pq-footer-height', footer.offsetHeight + 'px');
      }
    }

    // Listens for the EMBEDDED Klaviyo form's submit event.
    window.addEventListener('klaviyoForms', function (e) {
      if (!e.detail || e.detail.formId !== 'QXB938') return;
      if (e.detail.type === 'submit' && e.detail.metaData && e.detail.metaData.$email) {
        answers.email = e.detail.metaData.$email;
        submitQuiz();
      }
    });

    function refreshRequiredMessage(el) {
      var step = el.closest('[data-pq-step]');
      if (!step) return;
      var msg = step.querySelector('[data-pq-required-msg]');
      if (msg && !msg.hidden && validateStep(step)) {
        msg.hidden = true;
      }
    }

    root.querySelectorAll('[data-pq-custom-select]').forEach(function (wrap) {
      var trigger = wrap.querySelector('[data-pq-select-trigger]');
      var valueEl = wrap.querySelector('[data-pq-select-value]');
      var list = wrap.querySelector('[data-pq-select-options]');
      var options = wrap.querySelectorAll('.pq-select-option');
      var key = wrap.dataset.answerKey;

      function closeList() {
        list.hidden = true;
        wrap.dataset.open = 'false';
        trigger.setAttribute('aria-expanded', 'false');
      }
      function openList() {
        list.hidden = false;
        wrap.dataset.open = 'true';
        trigger.setAttribute('aria-expanded', 'true');
      }

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (list.hidden) openList(); else closeList();
      });

      options.forEach(function (opt) {
        opt.addEventListener('click', function () {
          options.forEach(function (o) { o.classList.remove('pq-selected'); });
          opt.classList.add('pq-selected');
          valueEl.textContent = opt.dataset.value;
          valueEl.classList.remove('pq-placeholder');
          wrap.classList.add('pq-has-value');
          answers[key] = opt.dataset.value;
          closeList();
          wrap.classList.remove('pq-field--error');
          refreshRequiredMessage(wrap);
        });
      });

      document.addEventListener('click', function (e) {
        if (!wrap.contains(e.target)) closeList();
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeList();
      });
    });

    root.querySelectorAll('select[data-pq-input], input[data-pq-input]').forEach(function (input) {
      input.addEventListener('change', function () {
        var key = input.dataset.answerKey;
        if (input.type === 'checkbox') {
          answers[key] = input.checked;
        } else {
          answers[key] = input.value;
        }
        clearError(input.closest('.pq-field'));
        refreshRequiredMessage(input);
      });
    });

    root.querySelectorAll('.pq-grid-select[data-pq-input]').forEach(function (group) {
      group.addEventListener('click', function (e) {
        var cell = e.target.closest('.pq-grid-cell');
        if (!cell) return;
        group.querySelectorAll('.pq-grid-cell').forEach(function (c) { c.classList.remove('pq-selected'); });
        cell.classList.add('pq-selected');
        answers[group.dataset.answerKey] = cell.dataset.value;
        refreshRequiredMessage(group);
      });
    });

    root.querySelectorAll('.pq-image-grid[data-pq-input]').forEach(function (grid) {
      var isMulti = grid.dataset.multi === 'true';
      var key = grid.dataset.answerKey;
      if (isMulti) answers[key] = answers[key] || [];

      var step = grid.closest('[data-pq-step]');
      var tipBox = step ? step.querySelector('[data-pq-tip]') : null;
      var tipText = tipBox ? tipBox.querySelector('[data-pq-tip-text]') : null;

      grid.addEventListener('click', function (e) {
        var card = e.target.closest('.pq-image-card');
        if (!card) return;
        var value = card.dataset.value;

        if (isMulti) {
          var list = answers[key] || [];
          if (card.classList.contains('pq-selected')) {
            card.classList.remove('pq-selected');
            list = list.filter(function (v) { return v !== value; });
          } else {
            card.classList.add('pq-selected');
            list.push(value);
          }
          answers[key] = list;
        } else {
          grid.querySelectorAll('.pq-image-card').forEach(function (c) { c.classList.remove('pq-selected'); });
          card.classList.add('pq-selected');
          answers[key] = value;

          var tip = card.dataset.tip;
          if (tipBox && tipText) {
            if (tip) {
              tipText.textContent = tip;
              tipBox.classList.add('pq-tip--visible');
            } else {
              tipBox.classList.remove('pq-tip--visible');
            }
          }
        }
        refreshRequiredMessage(grid);
      });
    });

    root.querySelectorAll('.pq-tabs').forEach(function (tabsEl) {
      var step = tabsEl.closest('[data-pq-step]');
      var panels = step.querySelectorAll('.pq-tab-panel');
      tabsEl.addEventListener('click', function (e) {
        var btn = e.target.closest('.pq-tab');
        if (!btn) return;
        var tabName = btn.dataset.pqTab;
        tabsEl.querySelectorAll('.pq-tab').forEach(function (t) { t.classList.remove('pq-tab--active'); });
        btn.classList.add('pq-tab--active');
        panels.forEach(function (p) {
          p.classList.toggle('pq-tab-panel--active', p.dataset.pqTabPanel === tabName);
        });
      });
    });

    root.querySelectorAll('[data-pq-tip-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        btn.closest('[data-pq-tip]').classList.remove('pq-tip--visible');
      });
    });

    function clearError(field) {
      if (field) field.classList.remove('pq-field--error');
    }

    function validateStep(step) {
      if (step.dataset.required === 'false') return true;
      var type = step.dataset.stepType;

      if (type === 'dropdown_step') {
        var wrap = step.querySelector('[data-pq-custom-select]');
        if (wrap && wrap.dataset.selectRequired !== 'false' && !wrap.classList.contains('pq-has-value')) {
          wrap.classList.add('pq-field--error');
          return false;
        }
      }

      if (type === 'tabs_step') {
        var activePanel = step.querySelector('.pq-tab-panel--active');
        if (!activePanel) return true;
        var groups = activePanel.querySelectorAll('.pq-grid-select[data-pq-input]');
        var allAnswered = true;
        groups.forEach(function (g) {
          if (!g.querySelector('.pq-grid-cell.pq-selected')) allAnswered = false;
        });
        return allAnswered;
      }

      if (type === 'image_grid_step') {
        var grid = step.querySelector('.pq-image-grid[data-pq-input]');
        return grid ? !!grid.querySelector('.pq-image-card.pq-selected') : true;
      }

      if (type === 'multiselect_step') {
        var mgrid = step.querySelector('.pq-image-grid[data-pq-input]');
        if (!mgrid) return true;
        return mgrid.querySelectorAll('.pq-image-card.pq-selected').length > 0;
      }

      if (type === 'email_step') {
        var input = step.querySelector('input[type="email"][data-pq-input]');
        return input ? !!input.value : true;
      }

      return true;
    }

    function goToStep(index) {
      steps.forEach(function (s, i) {
        s.classList.toggle('pq-step--active', i === index);
      });
      currentIndex = index;
      updateFooterHeightVar();
      root.dispatchEvent(new CustomEvent('pq:stepchange', { detail: { index: index } }));
    }

    root.querySelectorAll('[data-pq-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = btn.closest('[data-pq-step]');
        var msg = step.querySelector('[data-pq-required-msg]');

        if (!validateStep(step)) {
          if (msg) msg.hidden = false;
          return;
        }
        if (msg) msg.hidden = true;

        if (currentIndex === steps.length - 1) {
          submitQuiz();
        } else {
          goToStep(currentIndex + 1);
        }
      });
    });

    root.querySelectorAll('[data-pq-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (currentIndex > 0) goToStep(currentIndex - 1);
      });
    });

    root.querySelectorAll('[data-pq-restart]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        resultScreen.hidden = true;
        stepsWrapper.hidden = false;
        goToStep(0);
      });
    });

    root.querySelectorAll('[data-pq-back-to-quiz]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        resultScreen.hidden = true;
        stepsWrapper.hidden = false;
        goToStep(steps.length - 1);
      });
    });

    root.querySelectorAll('[data-pq-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        root.closest('.pq-modal-overlay') ? root.closest('.pq-modal-overlay').remove() : root.scrollIntoView({ behavior: 'smooth' });
      });
    });

    function submitQuiz() {
      if (answers.email) {
        saveEmailAnswer(answers.email, !!answers.newsletter_opt_in);
      }

      // Use the size-corrected band/cup instead of the raw step-2
      // answers, so the fit-check answers (Steps 3 & 6) actually
      // influence the final size and product match.
      var rawBand = answers[config.sizeAnswerKeys.band] || '';
      var rawCup = answers[config.sizeAnswerKeys.cup] || '';
      var adjusted = computeAdjustedSize(rawBand, rawCup);
      var band = adjusted.band;
      var cup = adjusted.cup;

      if (config.resultsPageUrl) {
        var params = new URLSearchParams();
        Object.keys(answers).forEach(function (key) {
          var val = answers[key];
          if (Array.isArray(val)) {
            val.forEach(function (v) { params.append(key, v); });
          } else if (val !== '' && val != null) {
            params.append(key, val);
          }
        });
        // Overwrite band/cup params with the adjusted values
        params.set(config.sizeAnswerKeys.band, band);
        params.set(config.sizeAnswerKeys.cup, cup);
        var sep = config.resultsPageUrl.indexOf('?') === -1 ? '?' : '&';
        window.location.href = config.resultsPageUrl + sep + params.toString();
        return;
      }

      var sizeLabel = (band + cup) || '—';
      stepsWrapper.hidden = true;
      resultScreen.hidden = false;
      var badge = resultScreen.querySelector('[data-pq-size-badge]');
      if (badge) badge.textContent = sizeLabel;

      fetchMatchingProducts(band, cup);
    }

    function saveEmailAnswer(email, optIn) {
      var form = document.getElementById('PQNewsletterForm-' + sectionId);
      if (form) {
        form.querySelector('[data-pq-newsletter-email]').value = email;
        form.querySelector('[data-pq-newsletter-marketing]').value = 'true';
        form.submit();
      }

      if (config.sendResultEmail && config.emailjsServiceId && config.emailjsTemplateId && config.emailjsPublicKey) {
        var adjusted = computeAdjustedSize(
          answers[config.sizeAnswerKeys.band] || '',
          answers[config.sizeAnswerKeys.cup] || ''
        );
        var band = adjusted.band;
        var cup = adjusted.cup;
        var sizeLabel = (band + cup) || '—';

        fetch('https://api.emailjs.com/api/v1.0/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            service_id: config.emailjsServiceId,
            template_id: config.emailjsTemplateId,
            user_id: config.emailjsPublicKey,
            template_params: {
              to_email: email,
              size: sizeLabel,
              band: band,
              cup: cup,
              shop_name: document.title,
              shop_url: window.location.origin
            }
          })
        }).catch(function () {
          // Silent fail — email delivery is a nice-to-have on top of the
          // Customer save above, which already succeeded regardless.
        });
      }
    }

    /* ============================
       KLAVIYO TRACKING
    ============================= */
    function sendQuizToKlaviyo(email, band, cup, products, resultUrl) {
      if (!window._learnq) {
        console.warn("Klaviyo is not loaded.");
        return;
      }

      var productData = products.map(function (product) {
        var productUrl = window.location.origin + "/products/" + product.handle;
        if (product._matchedVariantId) {
          productUrl += "?variant=" + product._matchedVariantId;
        }
        return {
          title: product.title,
          handle: product.handle,
          image: (product.images && product.images.length) ? product.images[0].src : "",
          url: productUrl
        };
      });

      _learnq.push(["identify", { "$email": email, "Bra Size": band + cup }]);
      _learnq.push(["track", "Bra Quiz Completed", {
        bra_size: band + cup,
        recommended_products: productData,
        quiz_result_url: resultUrl
      }]);
    }

    function fetchMatchingProducts(band, cup) {
      var grid = resultScreen.querySelector('[data-pq-product-grid]');
      var loading = grid.querySelector('[data-pq-loading]');

      var requiredFields = {};
      if (band) requiredFields[config.sizeAnswerKeys.band] = String(band);
      if (cup) requiredFields[config.sizeAnswerKeys.cup] = String(cup);
      (config.tagAnswerKeys || []).forEach(function (key) {
        var val = answers[key];
        if (!val) return;
        requiredFields[key] = Array.isArray(val) ? val.map(String) : String(val);
      });

      if (config.filterMode === 'metafields') {
        fetchMatchingProductsByMetafields(requiredFields, band, cup, grid, loading);
      } else if (config.filterMode === 'variants') {
        fetchMatchingProductsByVariants(band, cup, grid, loading);
      } else {
        fetchMatchingProductsByTags(requiredFields, band, cup, grid, loading);
      }
    }

    // ---- Variant-based matching ----
    function fetchMatchingProductsByVariants(band, cup, grid, loading) {
      var targetSize = (String(band) + String(cup)).toLowerCase().replace(/\s+/g, '');

      var url = config.collectionHandle
        ? '/collections/' + config.collectionHandle + '/products.json?limit=250'
        : '/products.json?limit=250';

      fetchAllProducts(url)
        .then(function (products) {
          // NEW: bras only — briefs/bikinis/etc. are dropped before any
          // matching happens.
          products = products.filter(isBraProduct);

          var matchedIds = {};

          var matches = products.filter(function (p) {
            var sizeOptionIndex = -1;
            (p.options || []).forEach(function (opt, idx) {
              if (opt.name && opt.name.toLowerCase().indexOf('size') !== -1) {
                sizeOptionIndex = idx;
              }
            });
            if (sizeOptionIndex === -1) return false;

            var optionKey = 'option' + (sizeOptionIndex + 1);
            var matchedVariant = (p.variants || []).find(function (v) {
              var val = (v[optionKey] || '').toLowerCase().replace(/\s+/g, '');
              return val === targetSize;
            });

            if (matchedVariant) {
              p._matchedVariantId = matchedVariant.id;
              matchedIds[p.id] = true;
              return true;
            }
            return false;
          });

          // Everything that wasn't an exact band+cup match — including
          // products with a different size format (crop ranges like
          // "8-10") and products with no size option at all — is kept
          // here instead of being dropped, so it can still show up.
          var others = products.filter(function (p) { return !matchedIds[p.id]; });

          // Soft-sort each group by Step 7-10 style preferences + the
          // other quiz answers (style/brand/etc.). Never excludes
          // anything — only reorders within each group.
          var preferenceTags = getPreferenceTags().concat(getTagAnswerPreferences());
          matches = sortByPreferenceTags(matches, preferenceTags);
          others = sortByPreferenceTags(others, preferenceTags);

          // Exact size matches first, then every other bra product after
          // them, so nothing in the catalog is ever fully excluded.
          var combined = matches.concat(others);
          var recommendedProducts = combined.slice(0, config.maxProducts || 4);
          renderProducts(recommendedProducts, grid, loading);

          if (answers.email) {
            var resultUrl = window.location.origin + window.location.pathname +
              '?' + config.sizeAnswerKeys.band + '=' + encodeURIComponent(band) +
              '&' + config.sizeAnswerKeys.cup + '=' + encodeURIComponent(cup);
            sendQuizToKlaviyo(answers.email, band, cup, recommendedProducts, resultUrl);
          }
        })
        .catch(function () {
          if (loading) loading.textContent = 'We could not load recommendations right now.';
        });
    }

    // ---- Tag-based matching ----
    function fetchMatchingProductsByTags(requiredFields, band, cup, grid, loading) {
      var requiredGroups = [];

      if (band && cup) {
        requiredGroups.push([String(band) + String(cup)]);
      } else if (band) {
        requiredGroups.push([String(band)]);
      } else if (cup) {
        requiredGroups.push([String(cup)]);
      }

      var url = config.collectionHandle
        ? '/collections/' + config.collectionHandle + '/products.json?limit=250'
        : '/products.json?limit=250';

      fetchAllProducts(url)
        .then(function (products) {
          // NEW: bras only.
          products = products.filter(isBraProduct);

          var matches = filterByTagGroups(products, requiredGroups);

          // Soft-sort by Step 7-10 style preferences + the other quiz
          // answers (style/brand/etc.) — ranks matches, never hides one.
          var preferenceTags = getPreferenceTags().concat(getTagAnswerPreferences());
          matches = sortByPreferenceTags(matches, preferenceTags);

          var recommendedProducts = matches.slice(0, config.maxProducts || 4);
          renderProducts(recommendedProducts, grid, loading);

          if (answers.email) {
            var resultUrl = window.location.origin + window.location.pathname +
              '?' + config.sizeAnswerKeys.band + '=' + encodeURIComponent(band) +
              '&' + config.sizeAnswerKeys.cup + '=' + encodeURIComponent(cup);
            sendQuizToKlaviyo(answers.email, band, cup, recommendedProducts, resultUrl);
          }
        })
        .catch(function () {
          if (loading) loading.textContent = 'We could not load recommendations right now.';
        });
    }

    // ---- Metafield matching (Shopify Storefront API / GraphQL) ----
    function fetchMatchingProductsByMetafields(requiredFields, band, cup, grid, loading) {
      if (!config.storefrontAccessToken) {
        if (loading) loading.textContent = 'Metafield matching requires a Storefront API access token in the section settings.';
        return;
      }

      var namespace = config.metafieldNamespace || 'custom';
      var keys = Object.keys(requiredFields);
      var identifiers = keys.map(function (key) {
        return '{namespace:' + JSON.stringify(namespace) + ',key:' + JSON.stringify(key) + '}';
      }).join(',');

      var query = config.collectionHandle
        ? 'query { collectionByHandle(handle: ' + JSON.stringify(config.collectionHandle) + ') { products(first: 250) { edges { node { ' + productFields(identifiers) + ' } } } } }'
        : 'query { products(first: 250) { edges { node { ' + productFields(identifiers) + ' } } } }';

      // `tags` is included so the metafield-matching path can also
      // participate in the soft preference sorting below.
      function productFields(ids) {
        return 'id title handle tags images(first: 1) { edges { node { url } } } ' +
          'variants(first: 1) { edges { node { title selectedOptions { name value } } } } ' +
          'metafields(identifiers: [' + ids + ']) { key value }';
      }

      fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.storefrontAccessToken
        },
        body: JSON.stringify({ query: query })
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          var edges = config.collectionHandle
            ? ((data.data && data.data.collectionByHandle && data.data.collectionByHandle.products.edges) || [])
            : ((data.data && data.data.products && data.data.products.edges) || []);

          var products = edges.map(normalizeGraphqlProduct);

          // NEW: bras only. GraphQL responses here don't include
          // product_type, so this relies on the tags array (already
          // requested above) via the same isBraProduct() check.
          products = products.filter(isBraProduct);

          // Only band+cup are enforced as hard requirements now.
          var sizeFields = {};
          if (band) sizeFields[config.sizeAnswerKeys.band] = String(band);
          if (cup) sizeFields[config.sizeAnswerKeys.cup] = String(cup);
          var matches = filterByMetafields(products, sizeFields);

          // Soft-sort by Step 7-10 style preferences + the other quiz
          // answers (style/brand/etc.) — ranks matches, never hides one.
          var preferenceTags = getPreferenceTags().concat(getTagAnswerPreferences());
          matches = sortByPreferenceTags(matches, preferenceTags);

          var recommendedProducts = matches.slice(0, config.maxProducts || 4);
          renderProducts(recommendedProducts, grid, loading);

          if (answers.email) {
            var resultUrl = window.location.origin + window.location.pathname +
              '?' + config.sizeAnswerKeys.band + '=' + encodeURIComponent(band) +
              '&' + config.sizeAnswerKeys.cup + '=' + encodeURIComponent(cup);
            sendQuizToKlaviyo(answers.email, band, cup, recommendedProducts, resultUrl);
          }
        })
        .catch(function () {
          if (loading) loading.textContent = 'We could not load recommendations right now.';
        });
    }

    function normalizeGraphqlProduct(edge) {
      var node = edge.node;
      var metafieldMap = {};
      (node.metafields || []).forEach(function (mf) {
        if (mf) metafieldMap[mf.key] = mf.value;
      });
      var imageEdge = node.images && node.images.edges && node.images.edges[0];
      var variantEdge = node.variants && node.variants.edges && node.variants.edges[0];
      var colour = '';
      if (variantEdge && variantEdge.node.selectedOptions) {
        var colourOpt = variantEdge.node.selectedOptions.find(function (o) {
          return /colou?r/i.test(o.name);
        });
        colour = colourOpt ? colourOpt.value : (variantEdge.node.selectedOptions[0] || {}).value || '';
      }
      return {
        handle: node.handle,
        title: node.title,
        tags: node.tags || [],
        _metafields: metafieldMap,
        images: imageEdge ? [{ src: imageEdge.node.url }] : [],
        variants: [{ option1: colour }]
      };
    }

    function filterByMetafields(products, requiredFields) {
      var keys = Object.keys(requiredFields);
      if (!keys.length) return products;
      return products.filter(function (p) {
        return keys.every(function (key) {
          var required = requiredFields[key];
          var actual = (p._metafields && p._metafields[key]) || '';
          var actualLower = actual.toLowerCase();
          if (Array.isArray(required)) {
            return required.some(function (r) { return String(r).toLowerCase() === actualLower; });
          }
          return String(required).toLowerCase() === actualLower;
        });
      });
    }

    // AND across groups, OR within a group — see comment above
    // fetchMatchingProductsByTags. requiredGroups now only ever contains
    // the size group, so this is effectively "match this size".
    function filterByTagGroups(products, requiredGroups) {
      if (!requiredGroups.length) return products;
      return products.filter(function (p) {
        var tags = (p.tags || []).map(function (t) { return String(t).toLowerCase(); });
        return requiredGroups.every(function (group) {
          return group.some(function (t) { return tags.indexOf(String(t).toLowerCase()) !== -1; });
        });
      });
    }

    function renderProducts(products, grid, loading) {
      if (loading) loading.remove();
      if (!products.length) {
        grid.innerHTML = '<p class="pq-loading">No exact matches found — try adjusting your answers.</p>';
        return;
      }
      grid.innerHTML = products.map(function (p) {
        var image = (p.images && p.images[0] && p.images[0].src) || '';
        var badge = p.tags && p.tags.indexOf('New') !== -1 ? '<span class="pq-product-badge">New</span>' : '';

        // Shows the ADJUSTED size (from the result badge), not the raw
        // step-2 answer, since that's what was actually matched on.
        var matchedSize = resultScreen.querySelector('[data-pq-size-badge]')
          ? resultScreen.querySelector('[data-pq-size-badge]').textContent
          : '';

        var productUrl = '/products/' + p.handle;
        if (p._matchedVariantId) {
          productUrl += '?variant=' + p._matchedVariantId;
        }

        return (
          '<div class="pq-product-card">' +
            '<a href="' + productUrl + '">' +
              badge +
              '<img src="' + image + '&width=500" alt="' + escapeHtml(p.title) + '" loading="lazy">' +
              '<p class="pq-product-colour">' + escapeHtml(matchedSize) + '</p>' +
              '<p class="pq-product-title">' + escapeHtml(p.title) + '</p>' +
            '</a>' +
          '</div>'
        );
      }).join('');
    }

    function escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }

    window.addEventListener('resize', updateFooterHeightVar);

    // If someone arrives via a "View all bras" link with size already in
    // the URL, skip straight to the results screen instead of restarting.
    var urlParams = new URLSearchParams(window.location.search);
    var bandParam = urlParams.get(config.sizeAnswerKeys.band);
    var cupParam = urlParams.get(config.sizeAnswerKeys.cup);
    if (bandParam || cupParam) {
      answers[config.sizeAnswerKeys.band] = bandParam || '';
      answers[config.sizeAnswerKeys.cup] = cupParam || '';
      stepsWrapper.hidden = true;
      resultScreen.hidden = false;
      var urlBadge = resultScreen.querySelector('[data-pq-size-badge]');
      if (urlBadge) urlBadge.textContent = ((bandParam || '') + (cupParam || '')) || '—';
      fetchMatchingProducts(bandParam || '', cupParam || '');
    } else {
      goToStep(0);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('.pq-wrapper').forEach(function (root) {
      try {
        initQuiz(root);
      } catch (err) {
        console.error('[product-quiz] failed to initialize:', err);
      }
    });
  });

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('.pq-wrapper');
    if (root) {
      try {
        initQuiz(root);
      } catch (err) {
        console.error('[product-quiz] failed to re-initialize:', err);
      }
    }
  });
})();